// ===========================================
// HEALTH CHECK HOOK
// ===========================================
// Polls the server health endpoint when disconnected
// Triggers reconnection when server becomes available
// ===========================================

import { useEffect, useRef } from 'react';
import { useSocketStore } from '../stores/socketStore';

const HEALTH_ENDPOINT = (import.meta.env.VITE_API_URL || 'http://localhost:3001') + '/api/health';
const POLL_INTERVAL = 5000; // 5 seconds

interface UseHealthCheckOptions {
  /** Whether health checking is enabled */
  enabled?: boolean;
  /** Callback when server becomes healthy */
  onHealthy?: () => void;
}

/**
 * Hook that polls the server health endpoint when disconnected.
 * Automatically triggers a reconnection when the server becomes available.
 */
export function useHealthCheck(options: UseHealthCheckOptions = {}) {
  const { enabled = true, onHealthy } = options;
  const { isConnected, isConnecting, isReconnecting, wasKickedFromOtherLocation, forceReconnect } = useSocketStore();

  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isPollingRef = useRef(false);
  const hasTriggeredReconnectRef = useRef(false);

  useEffect(() => {
    // Only poll when fully disconnected (not connected, not connecting, not reconnecting)
    // Don't poll if user was kicked from another location - they need to close the other tab
    const shouldPoll = enabled && !isConnected && !isConnecting && !isReconnecting && !wasKickedFromOtherLocation;

    if (!shouldPoll) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        isPollingRef.current = false;
      }
      // Reset the trigger flag when we're connected
      if (isConnected) {
        hasTriggeredReconnectRef.current = false;
      }
      return;
    }

    // Start polling
    const checkHealth = async () => {
      // Prevent concurrent checks and don't trigger multiple reconnects
      if (isPollingRef.current || hasTriggeredReconnectRef.current) return;

      try {
        isPollingRef.current = true;
        const response = await fetch(HEALTH_ENDPOINT, {
          method: 'GET',
          cache: 'no-store',
        });

        if (response.ok) {
          console.log('[useHealthCheck] Server is healthy, triggering reconnect');
          hasTriggeredReconnectRef.current = true; // Prevent further reconnect attempts
          onHealthy?.();
          forceReconnect();
        }
      } catch {
        // Server not available, keep polling
        console.log('[useHealthCheck] Server not available, will retry...');
      } finally {
        isPollingRef.current = false;
      }
    };

    // Check immediately
    checkHealth();

    // Then poll at interval
    pollIntervalRef.current = setInterval(checkHealth, POLL_INTERVAL);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [isConnected, isConnecting, isReconnecting, wasKickedFromOtherLocation, enabled, forceReconnect, onHealthy]);
}

export default useHealthCheck;
